// Pure helpers for the Columns wizard step: turning the server's column
// preview into an editable per-header selection and validating it before the
// user can confirm. Kept framework-free so the disabled-CTA logic is trivially
// testable.

export const IGNORE = 'ignore' as const

// Order matters: this is the option order rendered in each header's select.
export const NORMALIZED_COLUMNS = ['date', 'amount', 'description', 'balance'] as const
export const REQUIRED_COLUMNS = ['date', 'amount', 'description'] as const

export type NormalizedColumn = (typeof NORMALIZED_COLUMNS)[number]
export type ColumnChoice = NormalizedColumn | typeof IGNORE

export interface ColumnPreviewEntry {
  rawHeader: string
  mappedTo: string | null
  sample: string
}

// A header's current selection keyed by raw header. "ignore" means the header
// is dropped from the normalized CSV.
export type ColumnsValue = Record<string, ColumnChoice>

/** Seed the editable selection from the server preview (null mapping → ignore). */
export function columnsValueFromPreview(entries: ColumnPreviewEntry[]): ColumnsValue {
  const value: ColumnsValue = {}
  for (const e of entries) {
    value[e.rawHeader] =
      e.mappedTo && (NORMALIZED_COLUMNS as readonly string[]).includes(e.mappedTo)
        ? (e.mappedTo as NormalizedColumn)
        : IGNORE
  }
  return value
}

/** The raw-header → normalized-column map to submit (ignored headers dropped). */
export function columnsValueToMap(value: ColumnsValue): Record<string, string> {
  const map: Record<string, string> = {}
  for (const [rawHeader, choice] of Object.entries(value)) {
    if (choice !== IGNORE) map[rawHeader] = choice
  }
  return map
}

export interface ColumnsValidation {
  valid: boolean
  /** Required columns with no header assigned. */
  missing: NormalizedColumn[]
  /** Normalized columns assigned to more than one header. */
  duplicates: NormalizedColumn[]
}

/**
 * Confirm is allowed only when every required column is mapped exactly once and
 * no normalized column is claimed by two headers.
 */
export function validateColumnsValue(value: ColumnsValue): ColumnsValidation {
  const counts = new Map<NormalizedColumn, number>()
  for (const choice of Object.values(value)) {
    if (choice === IGNORE) continue
    counts.set(choice, (counts.get(choice) ?? 0) + 1)
  }

  const missing = REQUIRED_COLUMNS.filter((col) => !counts.get(col))
  const duplicates = NORMALIZED_COLUMNS.filter((col) => (counts.get(col) ?? 0) > 1)

  return { valid: missing.length === 0 && duplicates.length === 0, missing, duplicates }
}

/** Whether the edited selection differs from the server's applied map. */
export function columnsValueChanged(value: ColumnsValue, entries: ColumnPreviewEntry[]): boolean {
  const original = columnsValueFromPreview(entries)
  const keys = Object.keys(value)
  for (const key of Object.keys(original)) {
    if (!keys.includes(key)) keys.push(key)
  }
  return keys.some((key) => value[key] !== original[key])
}
